export type ChangelogEntry = {
  version: string;
  date: string;
  title: string;
  changes: string[];
};

export const APP_VERSION = "2.18.4";

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: "2.18.4",
    date: "2026-11-04",
    title: "Full Year load letters show each semester separately",
    changes: [
      "Fixed: Full Year load letters added both semesters' classes and allowances together and compared them with one fortnight's load, so many showed staff as heavily overloaded.",
      "A Full Year letter's Load Summary now has a Semester 1 and a Semester 2 column, each with its own allowances, classes, timetabled subs and utilisation.",
      "Full-year classes now count towards both semesters, so Semester 2 letters include them.",
    ],
  },
  {
    version: "2.18.3",
    date: "2026-11-04",
    title: "Staff on 0 FTE are no longer treated as full time",
    changes: [
      "Fixed: a staff member whose FTE is set to 0 (for example, on leave) was treated as 1.0 FTE across the app, including on load letters.",
      "Generate Letters now skips staff on 0 FTE and tells you who was skipped.",
    ],
  },
  {
    version: "2.18.2",
    date: "2026-11-04",
    title: "Regenerating load letters updates the letters you already have",
    changes: [
      "Generate Letters now starts on the semester that year's letters already use, so regenerating updates them instead of creating a second set.",
      "If you choose a different semester from the existing letters, you're asked to confirm first, because that creates a separate letter for each staff member.",
    ],
  },
  {
    version: "2.18.1",
    date: "2026-11-04",
    title: "Previous load letter versions only show letters you received",
    changes: [
      "Fixed: the Previous Versions list on My Load Letter could include letters that were generated but never released to you. It now only lists versions you actually received.",
    ],
  },
  {
    version: "2.18.0",
    date: "2026-11-04",
    title: "Edit load letter templates like a Word document",
    changes: [
      "The Letter Template page now has a visual editor for each section instead of HTML code boxes. Format text with headings, bold, italics, underline, colours, highlighting, bullet and numbered lists, tables, alignment and links.",
      "Add personalised details such as the staff member's name or FTE with the new Insert field button. They appear as blue tags and are filled in for each person when letters are generated.",
      "Sections typed as plain lines now open as proper paragraphs and bullet lists.",
    ],
  },
  {
    version: "2.17.8",
    date: "2026-11-04",
    title: "Load letters only show that year's allowances",
    changes: [
      "Fixed: load letters listed allowances from every year, so 2027 letters showed each allowance twice (the 2026 one and the 2027 one) and overstated allowance minutes and utilisation. Regenerate affected letters to correct them.",
    ],
  },
  {
    version: "2.17.7",
    date: "2026-11-03",
    title: "Internal improvements",
    changes: ["No user-facing changes in this release."],
  },
  {
    version: "2.17.6",
    date: "2026-11-02",
    title: "Internal improvements",
    changes: ["No user-facing changes in this release."],
  },
  {
    version: "2.17.5",
    date: "2026-11-02",
    title: "Internal improvements",
    changes: ["Behind-the-scenes data access update; no visible change."],
  },
  {
    version: "2.17.4",
    date: "2026-10-26",
    title: "Internal improvements",
    changes: ["No user-facing changes in this release."],
  },
  {
    version: "2.17.3",
    date: "2026-10-09",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "2.17.2",
    date: "2026-10-09",
    title: "Security fix for role and AI settings",
    changes: [
      "Only administrators can now create, change or delete roles and their permissions. Other staff can still see them.",
      "AI (Gemini) settings are now private to each person, and API keys are never sent back to the browser.",
    ],
  },
  {
    version: "2.17.1",
    date: "2026-10-02",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "2.17.0",
    date: "2026-09-17",
    title: "New: Export for Karen",
    changes: [
      "The grid editor's More actions menu has a new 'Export for Karen' option. It downloads one spreadsheet laid out the way the timetabler works: a Classes sheet with a teacher under each line (A to L, plus V and W for Wednesday VET) and a RUAH sheet with a column per term.",
    ],
  },
  {
    version: "2.16.3",
    date: "2026-09-11",
    title: "Editing a staff member no longer undoes changes made elsewhere",
    changes: [
      "Saving the Edit Staff form used to write back every field exactly as it looked when you opened the form. If something had changed in the meantime — an FTE applied from the intention review, say — saving would quietly put the old value back, which looked like your change not saving.",
      "It now sends only the fields you actually changed, so saving one thing cannot undo another.",
    ],
  },
  {
    version: "2.16.2",
    date: "2026-09-11",
    title: "A staff change that doesn't take now says which field refused",
    changes: [
      "There is a fault where editing a staff member appears to work but a field keeps its old value. It is being investigated.",
      "In the meantime the app now checks each field after saving and tells you exactly which one did not take, instead of reporting success. If you hit it, please send that message through — it names the field and will pinpoint the cause.",
    ],
  },
  {
    version: "2.16.1",
    date: "2026-09-11",
    title: "A staff edit that doesn't save now tells you",
    changes: [
      "If a change to a staff member could not be saved because of a permissions rule, the app used to report 'updated successfully' anyway and the change quietly disappeared on the next refresh.",
      "It now checks whether the record actually changed, and tells you plainly if it did not, so you are not left wondering whether it worked.",
    ],
  },
  {
    version: "2.16.0",
    date: "2026-09-11",
    title: "0.9 FTE can now be chosen, and 'discuss' no longer reads as a request",
    changes: [
      "The teaching intentions form had no 0.9 option, so anyone wanting it had to pick 'I'd like to discuss my options' instead. 0.9 is now on the list.",
      "Choosing 'I'd like to discuss my options' was being recorded as a request for full time. The FTE review screen then offered to set those people to 1.0, which is the opposite of what they asked for. They are now left out of that list and stay in the discuss queue, where they belong.",
      "For 2027 that removes 8 people from the FTE review list who should never have been on it, leaving 4 genuine ones.",
    ],
  },
  {
    version: "2.15.1",
    date: "2026-09-11",
    title: "Editing allowances from the Staff Directory now works per year",
    changes: [
      "The allowances section inside Edit Staff was showing every year at once, so each allowance appeared twice with nothing to tell the copies apart. It now shows only the year you are working in, and leaves the other years untouched when you save.",
      "Allowances added there are now recorded against that year. Previously they had no year at all, which meant they counted towards every year including ones already finished.",
      "Saving a staff member no longer resets their teaching base to the standard full-time figure, which would have wiped a reduced base for anyone who has one.",
    ],
  },
  {
    version: "2.15.0",
    date: "2026-09-11",
    title: "Teaching loads now agree across every screen",
    changes: [
      "The Staff Directory and the Timetable Editor were working out teaching loads differently, so the same teacher could show a different load on each. They now use one shared calculation.",
      "The Staff Directory was counting every class as 440 minutes regardless of its real length, and ignoring classes shared between two teachers altogether. Both are now counted properly.",
      "The Timetable Editor was counting RUAH classes at 440 minutes each when a RUAH class is 210, and was counting composite classes twice. RUAH loads in particular will look noticeably lower — that is the correction, not a loss.",
    ],
  },
  {
    version: "2.14.4",
    date: "2026-09-11",
    title: "The Timetable Editor was counting allowances twice as well",
    changes: [
      "The load figures in the Timetable Editor — the teacher panels and the colour of each cell — were subtracting everyone's allowances twice, for the same reason the Staff Directory was. Staff looked far busier than they are, and some showed as overloaded when they are not.",
      "That calculation also left out allowances marked as applying to both semesters, so some people's figures were wrong in the other direction too.",
      "Both screens now use one shared calculation, so they cannot drift apart again.",
    ],
  },
  {
    version: "2.14.3",
    date: "2026-09-11",
    title: "Staff Directory teaching load was counting allowances twice",
    changes: [
      "The Teaching Load column in the Staff Directory was subtracting each person's allowances twice over, because allowances now exist for both 2026 and 2027 and that column was adding up every year at once. It made people look far busier than they are, and some show as overloaded when they are not.",
      "It now counts only the year you are looking at, matching every other screen.",
    ],
  },
  {
    version: "2.14.2",
    date: "2026-09-11",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release. PIT now has a working automated test suite for the first time; the one test file in the project could never actually run before.",
    ],
  },
  {
    version: "2.14.1",
    date: "2026-09-11",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release. Sixteen classes in the 2027 grids were missing the record of which lines they are allowed to run on, so the Timetable Editor had to guess from where they were already staffed. That record has been restored from the subject availability configuration.",
    ],
  },
  {
    version: "2.14.0",
    date: "2026-09-11",
    title: "Staffing a grid no longer loses other people's work",
    changes: [
      "Fixed a serious bug that could silently delete teacher allocations. Saving one allocation used to rewrite every class in the grid from whatever your browser had loaded, so if your tab was out of date it quietly wiped allocations made since — with no error and no warning.",
      "Allocations lost to this bug in the 2027 Main Grid have been restored from the assignment history: 181 allocations came back, including a full afternoon of English staffing.",
      "Each allocation is now saved on its own, directly against the class you clicked. Nothing else in the grid is touched, so two people can staff different learning areas at the same time without overwriting each other.",
      "If someone else changes the grid's structure while you are editing it, you now get a clear message and the latest version is reloaded, instead of your save silently winning.",
    ],
  },
  {
    version: "2.13.3",
    date: "2026-09-11",
    title: "Inactive accounts get an explanation instead of an empty app",
    changes: [
      "Someone whose staff record is marked inactive could sign in successfully and then find every page blank, with nothing saying why. PIT resolves what you can see from your active staff record, so an inactive one means no data at all.",
      "They now get a clear message that the account is inactive and to contact an administrator, rather than a working login onto an empty app.",
    ],
  },
  {
    version: "2.13.2",
    date: "2026-09-11",
    title: "The impersonation banner now says what it does and does not show",
    changes: [
      "When an admin views the app as another staff member, the banner now states that this shows that person's view but not their permissions — data is still read with the admin's own access.",
      "Previously it said only who you were impersonating, which could be read as 'you are now seeing exactly what they see'. Anyone using it to check whether a teacher could reach something would have got the wrong answer.",
    ],
  },
  {
    version: "2.13.1",
    date: "2026-09-11",
    title: "Empty reports now say why they are empty",
    changes: [
      "When a report had no rows it always said 'try a different filter' — even when the school had no staff imported at all, where no filter would ever have helped. That reads as a broken report.",
      "The FTE & Days Off and Staff FTE reports now distinguish the cases: no staff on record at all, staff on record but none in the chosen group, or a learning area with nobody in it. Each says what would actually fix it, with the counts.",
    ],
  },
  {
    version: "2.13.0",
    date: "2026-09-11",
    title: "Staff codes can be corrected",
    changes: [
      "New staff get an automatically generated staff code, and until now there was no way to change it if it was wrong. You can now edit it from User Management.",
      "The form rejects a code already used by someone else, and explains rather than showing a database error.",
      "It also warns before you save: grid allocations, load letters and teaching intentions all refer to a person by their code, so changing it detaches everything recorded against the old one.",
    ],
  },
  {
    version: "2.12.3",
    date: "2026-09-11",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release. Fixed the AI settings save path, which could never have worked — it tried to store 25 settings in columns that do not exist, and reading a saved record would have wiped the rest back to blank.",
    ],
  },
  {
    version: "2.12.2",
    date: "2026-09-11",
    title: "No more duplicate entries in the allowance type list",
    changes: [
      "The allowance type list showed two identical 'Year 8 Learning Leader' entries and two identical 'POL 1' entries, with no way to tell which was which. The redundant copies have been retired.",
      "Retiring an allowance type now actually removes it from the list when adding an allowance. Previously it made no difference, which is how the duplicates built up. Allowances already using a retired type keep working and can still be edited.",
    ],
  },
  {
    version: "2.12.1",
    date: "2026-09-11",
    title: "Two intention periods can no longer clash",
    changes: [
      "It was possible to create two intention periods with the same name for the same year, which left two identical entries in the period picker with no way to tell them apart.",
      "It was also possible to have two periods open for one year at once — which happened, with one left open for four months after it should have closed. Only one period per year can be open now.",
      "If you try either, you now get a plain explanation of which rule you hit instead of a database error.",
    ],
  },
  {
    version: "2.12.0",
    date: "2026-09-11",
    title: "Classes can be added to a second line, and requested FTEs can be applied",
    changes: [
      "In the Timetable Editor, a class that was already assigned to one line could not be put on any other — the other columns had no cell to click at all. Adding DISCOVER - HPE to lines B and C was impossible once line A was filled. Every line now offers a slot. Fifteen classes in the 2027 grid were stuck this way.",
      "Eighteen classes in the 2027 Main Grid were counting as 70 minutes instead of 440, because that grid had no default class length recorded. It now does, so those classes carry their real load.",
      "Teaching intentions have never updated anyone's FTE — the import only ever set it when creating a brand new staff member, so 47 staff were left on an out-of-date figure. HR → Intention Review now lists everyone whose requested FTE differs from their record and lets you apply them, one at a time or all at once.",
      "A class in the 2026 Wednesday VET grid was invisible in the app because two copies of the grid's class list had drifted apart. They are back in step.",
    ],
  },
  {
    version: "2.11.0",
    date: "2026-09-10",
    title: "Allowances are now recorded against an academic year",
    changes: [
      "Allowances used to have no year. There was one set per staff member for all time, so entering a 2027 allowance would have quietly overwritten their 2026 one and there would have been no way to get it back.",
      "Staff Allowances now has a year selector, and the Add Allowance form tells you which year it is about to record against. 'Applies To' still chooses Semester 1, Semester 2 or both — but now within the year you are looking at.",
      "Existing allowances have been recorded against 2026 and copied to 2027, so 2027 starts from where 2026 finished. Review the 2027 copies and adjust them rather than starting from a blank page.",
      "Teaching Load Summaries, the Staff Directory, the Teacher Loads drawer and conflict checking all now count only the allowances belonging to the year on screen.",
    ],
  },
  {
    version: "2.10.1",
    date: "2026-09-10",
    title: "Teaching Load Summaries now show the right minutes for 2027",
    changes: [
      "Teaching Load Summaries was counting every 2027 class as 70 minutes instead of its real length, so a teacher carrying a full load appeared to be at about 16% instead of nearly 100%. The page now reads each class's actual minutes. 2026 figures are unchanged, and no released load letter is affected.",
      "The same miscount was fixed in Workload Equity, the reports, and load letter generation, so 2027 letters will be correct when they are generated.",
      "You can now switch academic year directly from the Teaching Load Summaries page instead of having to change it somewhere else first.",
      "Staff who have left no longer appear in the list as empty 0% rows, and they no longer clutter the 'underutilised' filter.",
      "Staff with a reduced teaching base are now measured against their own base rather than the standard full-time one, so their utilisation reads correctly.",
      "The page now states that allocations are for the selected year but FTE and allowances are each person's current figures, since those are not yet stored per year.",
    ],
  },
  {
    version: "2.10.0",
    date: "2026-09-07",
    title: "Search for a subject by name in the grid editor and intention review",
    changes: [
      "The grid editor has a new Search box. Type part of a subject name — \"media\", for example — and the grid narrows to every matching class, without needing to know which learning area it sits under. It matches the class name, the subject code and the learning area.",
      "Intention review now searches what staff asked to teach, not just who they are. Searching \"media\" finds everyone who listed a Media subject or learning area among their preferences.",
      "Intention review had always offered to search by learning area, but only ever looked at staff name, code and department. It now does what it says.",
    ],
  },
  {
    version: "2.9.6",
    date: "2026-09-04",
    title: "The grid editor now shows every line, not just A to F",
    changes: [
      "The grid editor was only ever drawing lines A to F, so any class timetabled on lines G to L was missing from the screen. Filtering the 2027 Main Grid to Arts counted 68 subjects but could only show 37 of them — the other 31 sit on G to L and had nowhere to appear.",
      "This affected every curriculum area in the 2027 grid, not just Arts, and it happened with no filters applied.",
      "The editor now works out a grid's lines from the classes it actually contains, so it can never show fewer lines than the grid uses.",
    ],
  },
  {
    version: "2.9.5",
    date: "2026-09-04",
    title: "The grid editor now opens the grid for the year you selected",
    changes: [
      "The grid editor could open on a different year's grid than the one you had selected — it always started on the first grid by name across every year, so with 2027 chosen you could land on the 2026 Main Grid and see no teaching intentions against your 2027 staff.",
      "Choosing a year now selects that year's grid, keeping the classes, the teacher preference panels and the year selector in agreement.",
      "Fixes a problem introduced in 2.9.4, where opening the editor could quietly change your selected year to the year of whichever grid it had opened.",
    ],
  },
  {
    version: "2.9.4",
    date: "2026-09-03",
    title: "The grid editor now shows the intentions for the grid you have open",
    changes: [
      "Opening a grid and seeing \"No teaching intention submitted for this year\" against every teacher is fixed. The editor was reading teaching intentions for whichever year your year selector happened to be set to, rather than the year of the grid in front of you — so the same 2027 grid showed all the submitted intentions for one person and none for another.",
      "Opening a grid now switches your working year to that grid's year, so the header, the teacher preference panels and auto-assign all agree with the grid you are editing.",
    ],
  },
  {
    version: "2.9.3",
    date: "2026-09-02",
    title: "The 2027 grid now shows every year level, not just VCE",
    changes: [
      "The 2027 Main Grid has been rebuilt from the timetabling team's latest grid. It now holds all 974 classes across Years 7 to 12 — the Discover, Explore, Engage, Focus and Reach programs are there for the first time, alongside an updated VCE set.",
      "Some VCE classes have moved lines, a few subjects gained or lost a class, and ACU Foundation and English ACU have been added. Politics Units 1 and 2 is no longer offered in 2027.",
      "The 2027 Wednesday VET grid has been refreshed from the same source and now holds 14 classes.",
      "STRETCH, ACU Foundation and the Spare line now appear as their own learning areas so they no longer sit under an unrelated faculty.",
      "No teaching allocations were changed — every class is still unstaffed.",
    ],
  },
  {
    version: "2.9.2",
    date: "2026-09-01",
    title: "Fixed \"Could not build submission\" when filling an intention on behalf",
    changes: [
      "Some administrators could not submit an intention on someone's behalf — the form failed with \"Could not build submission\". The cause was their own staff profile having no staff code, which the app needs to record who entered the form. Affected profiles have been corrected.",
      "New staff members are now given a staff code when they are added, instead of being created without one. The code is shown in the confirmation message when you add someone, so you can check it matches the code the college normally uses for them.",
      "When something does block a submission, the message now says what is actually wrong instead of a generic failure.",
    ],
  },
  {
    version: "2.9.1",
    date: "2026-09-01",
    title: "Newly added staff show up straight away",
    changes: [
      "When an administrator adds a staff member under User Management, that person now appears immediately everywhere else in the app — the staff directory, allowances, and the list of people you can submit an intention on behalf of. Previously you had to reload the page in your browser before they turned up.",
      "The same applies when you change someone's role or status, or remove them.",
    ],
  },
  {
    version: "2.9.0",
    date: "2026-09-01",
    title: "Fill an intention on behalf of staff, even after the period closes",
    changes: [
      "Administrators and assistant principals can now use \"Fill on behalf\" at any time — it no longer requires a submission period to be open. That was the wrong way round, because the button exists mostly for people who missed the window.",
      "The page now asks which submission period the form belongs to, listing every period with whether it is open, closed or archived. If a period is currently open, it is chosen for you.",
      "A note on screen explains that a submission recorded after the period's due date is still marked late, so the reporting stays honest.",
    ],
  },
  {
    version: "2.8.4",
    date: "2026-08-31",
    title: "Removed an unused setup page",
    changes: [
      "Removed an old first-time setup page for creating the very first administrator. It had stopped working some time ago, it was reachable without signing in, and administrators are already managed under Admin → User Management.",
    ],
  },
  {
    version: "2.8.3",
    date: "2026-08-28",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release.",
      "Behind the scenes: every proposed change to PIT is now automatically built before it can be merged, so a change that would stop the app building is caught before it reaches you.",
    ],
  },
  {
    version: "2.8.2",
    date: "2026-08-28",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release.",
      "Behind the scenes: the AI (Gemini) configuration record could never have been saved — the database had no way to tell one person’s settings from another’s, so any attempt would have been rejected outright. Nothing was lost, because nothing had ever been saved. That is now fixed ahead of the settings screen being wired up.",
    ],
  },
  {
    version: "2.8.1",
    date: "2026-08-31",
    title: "Health & PE leader can see their classes again",
    changes: [
      "Fixed the Health & PE leader opening Teacher Assignments and Grid Assignments to an empty screen. Their classes were there the whole time — PIT was looking for them under the wrong name.",
      "The cause: a leader’s learning area is recorded as ‘Health & PE’ on their staff record, but the same area is written as ‘HPE’ against classes on the grid, and the two were being compared letter for letter.",
      "Every learning area is now matched by what it means rather than how it is spelled, so this cannot happen again to another area.",
      "No change to what anyone else sees: every other leader still sees exactly their own area, and the Vocational Major leader keeps seeing VET classes and the whole Wednesday VET grid.",
    ],
  },
  {
    version: "2.8.0",
    date: "2026-08-27",
    title: "Auto-assign: one button that applies every rule",
    changes: [
      "Auto-assign now opens with 'All rules' — it works through every rule at once and gives you a single list in priority order, instead of asking you to pick a mode first.",
      "The three original options are still there underneath if you want to look at one rule at a time.",
      "This matters because the old options overlapped: running 'Personal preferences' on the 2027 grid missed every teacher a learning area leader had nominated who hadn't also asked for the subject themselves.",
      "Combining the rules doesn't weaken the safety checks — a teacher still can't be double-booked on a line, two teachers can't be put in the same slot, and load limits still apply.",
    ],
  },
  {
    version: "2.7.3",
    date: "2026-08-27",
    title: "Staff preferences now shows what teachers actually asked for",
    changes: [
      "The staff preferences panel in the Timetable Editor was listing subjects PIT had worked out for a teacher under the heading 'Teaching Preferences', so a suggestion looked identical to something the teacher had asked for by name.",
      "It now shows 'They asked for' first — their ranked subjects, RUAH picks and learning areas, exactly as they submitted them.",
      "PIT's own suggestions are still there, below a divider, clearly labelled as worked out from the grid rather than requested.",
      "Where a teacher named only learning areas and no specific subjects, the panel now says so — that's the case where suggestions are weakest.",
    ],
  },
  {
    version: "2.7.2",
    date: "2026-08-27",
    title: "Fix: saving a teacher nomination works",
    changes: [
      "Curriculum leaders got an error when saving a nomination. Saving now works.",
      "Nothing that was entered before is lost — the saves were rejected outright rather than partly written.",
    ],
  },
  {
    version: "2.7.1",
    date: "2026-08-27",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "2.7.0",
    date: "2026-08-27",
    title: "Auto-assign now follows the school's priority order",
    changes: [
      "Auto-assign suggestions are now ranked in the school's order: a teacher who asked for the subject, whose learning area leader also nominated them, and who taught it last year comes first; a teacher who only named the learning area comes last.",
      "Each suggestion says which priority it is and why, in plain English, so you can see the reasoning before you apply anything.",
      "'Taught it last year' is read from last year's timetable grids.",
      "Applying suggestions is now a single save. Previously each one was saved separately, which was slow on a large grid and could leave a half-finished batch behind if something went wrong.",
      "If a batch fails, PIT now says clearly that nothing was assigned and why. It previously reported success even when some assignments had failed.",
      "Auto-assign now re-checks you're on the right grid and year at the moment you apply, not just when you search.",
      "A learning area leader marking a teacher as 'avoid' for a subject is now respected everywhere, including when suggesting teachers who asked for it themselves.",
    ],
  },
  {
    version: "2.6.1",
    date: "2026-08-27",
    title: "The nomination page now tells you which year you're nominating for",
    changes: [
      "Teacher Assignments now shows the year in the page title — 'English Teacher Assignments — 2027' — so there's no doubt about which year you're nominating for.",
      "There's now a year selector on the page itself. It was previously only available inside the Timetable Editor, so leaders had no way to see or change the year.",
      "The subject count and the save confirmation both name the year too.",
      "When your learning area has no subjects for the year yet, the page now says so instead of showing an empty table.",
    ],
  },
  {
    version: "2.6.0",
    date: "2026-08-27",
    title: "Year 12 classes are in, and teacher nomination now works",
    changes: [
      "The 2027 Year 12 (Units 3 & 4) classes have been added — 152 to the Main Grid and 12 to Wednesday VET. The Main Grid now holds 346 classes.",
      "Curriculum leaders can now save who they want teaching each class. Saving previously failed every time, so no nomination had ever been stored.",
      "The nomination screen now follows the year selector. It was showing this year's subjects and this year's saved nominations while you thought you were planning next year.",
      "Nominations are now recorded against the teacher's real staff code, so they actually show up when you assign teachers. Nominating someone who has no staff code is now refused with an explanation instead of saving silently.",
      "Saved nominations no longer appear as red 'Avoid' chips when they were actually someone's top pick.",
      "Auto-assign no longer suggests putting a class on a line it isn't allowed to run on — that was 57% of its suggestions — and no longer suggests a teacher who is already teaching on that line.",
      "Auto-assign now respects real teaching loads, so it won't push anyone over their available minutes. It previously treated everyone as having unlimited capacity.",
      "The auto-assign confirmation now shows the line each suggestion would use, and nothing is pre-ticked — you choose what to apply.",
      "Auto-assign no longer draws on unsubmitted draft intentions or inactive staff.",
    ],
  },
  {
    version: "2.5.5",
    date: "2026-08-27",
    title: "Fix: learning-area preferences now match the right classes",
    changes: [
      "A teacher who nominated a learning area rather than specific subjects — 35 of this year's submissions — barely matched anything. PIT was guessing a class's area from its name instead of using the area the grid already records, so 'Arts' never matched a class called DRAMA12.",
      "Learning-area preferences now match on the area recorded against the class. 'Health & PE' on the intentions form and 'HPE' on the grid are treated as the same area.",
      "Fixed teachers being offered for unrelated subjects: anyone who nominated Health & PE was showing as a strong match for Specialist Mathematics, because 'PE' appears inside the word 'specialist'.",
      "Match reasons now name a learning-area match as such, so you can see why a teacher was suggested.",
    ],
  },
  {
    version: "2.5.4",
    date: "2026-08-27",
    title: "Fix: teachers' subject preferences now show when you assign them",
    changes: [
      "Every teacher card in the assignment window said 'No teaching preferences recorded', even for staff who had ranked their subjects and learning areas on the intentions form. Their preferences are now read and shown.",
      "This affected all 190 intentions submitted for 2027. Ranked subjects, ranked learning areas and RUAH nominations all come through, in the order the teacher put them.",
      "Preference stars and match strength in the Timetable Editor work again, so sorting teachers by best fit is meaningful.",
      "When a teacher has preferences but none apply to the class you're filling, the card now says 'No preference for this subject' instead of claiming nothing was recorded.",
      "Nothing anyone submitted has changed — PIT was reading the wrong part of their form.",
    ],
  },
  {
    version: "2.5.3",
    date: "2026-08-27",
    title: "Fix: teacher loads no longer count last year's classes",
    changes: [
      "When assigning teachers in the 2027 grid, everyone appeared to already have minutes on them even though no 2027 class had been assigned yet. PIT was adding up every grid it had, so it was showing 2026 loads.",
      "The minutes shown in the teacher assignment window, the Teacher Loads drawer and the Teaching Load Summaries page now count only the year you are working in — the year shown in the year selector.",
      "Teaching Load Summaries now says which year it is reporting on.",
      "Teaching intentions in the Timetable Editor now follow the year selector too. They were locked to 'next calendar year', which was right this year by luck and would have started showing nothing from January.",
      "No assignments or intentions were changed — only the totals PIT was displaying.",
    ],
  },
  {
    version: "2.5.2",
    date: "2026-08-27",
    title: "Fix: the subject list in Teaching Preferences is complete again",
    changes: [
      "The 'Specific Subjects' picker in Teaching Preferences was showing only the subjects already placed on next year's timetable grid. While that grid is still being built, most subjects were missing and could not be nominated.",
      "It now lists next year's subjects together with this year's, so every subject we currently offer is there alongside anything new for next year.",
      "Nothing you have already submitted or saved has changed.",
    ],
  },
  {
    version: "2.5.1",
    date: "2026-08-25",
    title: "2027 Wednesday VET classes are in",
    changes: [
      "The 2027 Wednesday VET grid now holds all 36 VET classes, with 102 students across them.",
      "It now has four lines instead of two — X AM, X PM, Y AM and Y PM — so morning and afternoon sessions stay separate the way the timetabling export has them.",
      "Its learning-area list was showing a few names that don't exist elsewhere in PIT; it now uses the same 15 areas as every other grid.",
      "Note: the 25 externally-run VET courses (Plumbing, Baking, Electrotechnology and so on) can't be assigned a teacher, so they'll show as unallocated. Marking them properly is coming.",
    ],
  },
  {
    version: "2.5.0",
    date: "2026-08-25",
    title: "Timetable Editor: one row per subject",
    changes: [
      "The Timetable Editor now groups a subject's classes into a single row, so you can see and fill all of Biology's line allocations at once instead of scrolling through six near-empty rows. The 2027 Main Grid drops from 194 rows to 50.",
      "Where a subject runs two or three classes on the same line — English on line C, Religion on line C and I — the cell splits into one slot per class and the row grows taller, so nothing is squashed or hidden.",
      "Each slot still works exactly as before: click to assign, the same status colours, flags and multi-teacher breakdowns.",
      "The row tick box selects every class in the subject, so Bulk Assign works straight from the grouped view.",
      "Turn it off with the new 'Group by Subject' tick box in the toolbar to go back to one row per class. Your choice is remembered.",
      "Composite classes and their sub-classes keep their own rows, so teaching loads are still counted the same way.",
    ],
  },
  {
    version: "2.4.1",
    date: "2026-08-25",
    title: "Building a grid now actually saves",
    changes: [
      "Classes added to a grid that was copied forward to a new year no longer disappear. They were being saved, but the grid read them from a different place and showed none — the two are now kept together.",
      "Building a grid from a class list that includes a Line column no longer fails at the last step with a database error.",
      "The Import Grid page saves again. Importing subjects had been failing outright, and could also reset the grid's class length to 70 minutes.",
      "Importing subject-and-line setup on the Line & Subject Setup page saves again.",
      "Flagging a cell in the Timetable Editor saves again.",
      "Private review notes on a teaching intention save again.",
      "Changing a grid's semesters through the grid editor is no longer silently ignored.",
    ],
  },
  {
    version: "2.4.0",
    date: "2026-08-21",
    title: "FTE & Days Off: see who asked for part-time without naming a day",
    changes: [
      "The FTE & Days Off report now names the staff who asked for a part-time load but haven't said which day they want off — whether they chose 'no preference', asked to discuss it, or skipped the question.",
      "A new tile across the top shows how many people that is and how many days a week are still unplaced.",
      "The planning summary lists them with their current and requested FTE, the days a week they're owed, and what they actually answered. Click anyone for their full response.",
      "Staff who named some but not all of their days off (for example 0.6 with only Wednesday chosen) are called out underneath, so partial answers don't slip through.",
      "The learning-area table has a 'No day named' column, and the staff list has a matching 'Part-time, no day named' filter.",
      "Exports to CSV, Excel and PDF include the new flag.",
    ],
  },
  {
    version: "2.3.0",
    date: "2026-08-18",
    title: "New report: FTE & Requested Days Off",
    changes: [
      "A new report under Reports shows what each staff member is contracted for now, the load they asked for next year, and which days they want off.",
      "The planning summary shows the school's total FTE now versus requested, the net change, and how many people are still to answer or have asked to discuss their options.",
      "A weekday breakdown shows how many staff want each day off, with the names under each day, so you can see where the timetable will be tight before you build it.",
      "Days off that nobody has pinned to a weekday yet — where someone chose 'no preference' or asked to discuss — are counted separately so they don't get lost.",
      "A per-learning-area table shows where load is moving and which areas carry the most days off to cover.",
      "Click any staff member for their full answer: current and requested FTE, days off, return intention, leave plans and comments.",
      "The staff list can be searched, filtered (reducing load, needs a conversation, no response, and more) and exported to CSV, Excel or PDF.",
    ],
  },
  {
    version: "2.2.3",
    date: "2026-08-17",
    title: "Fix: your role is no longer stuck as Teacher after a hiccup",
    changes: [
      "If PIT briefly couldn't read your staff record when you signed in, it fell back to a basic Teacher profile and then kept using it on every later visit — even once the problem had passed. It now retries and picks up your real role.",
      "Anyone currently stuck on the wrong role will be corrected automatically the next time they open PIT. No need to clear your browser data.",
    ],
  },
  {
    version: "2.2.2",
    date: "2026-08-17",
    title: "Fix: launching PIT for a school no longer signs you straight back out",
    changes: [
      "Opening PIT for a school from the central platform kept signing you out again a second or two after it loaded. It now stays signed in.",
      "Your role is recognised correctly on arrival, so admins land on the full dashboard instead of the teacher view.",
      "Launching a school no longer signs you out of PIT in other browser tabs.",
    ],
  },
  {
    version: "2.2.1",
    date: "2026-08-17",
    title: "Fix: launching PIT from the platform now switches schools cleanly",
    changes: [
      "When opening PIT for a specific school from the central platform, the app now fully switches to that school instead of keeping your previous session. Your cached profile is cleared so the correct school's data loads.",
    ],
  },
  {
    version: "2.2.0",
    date: "2026-08-17",
    title: "Multi-school support: launch PIT for a specific school from the platform",
    changes: [
      "PIT can now run for more than one school. Each school sees only its own staff, grids, load letters and settings, with its own branding.",
      "Added a platform sign-in path (/auth/sso) so opening PIT from the central platform's \"Open as this school\" now signs you into that school, not your own.",
    ],
  },
  {
    version: "2.1.23",
    date: "2026-08-16",
    title: "Internal improvements",
    changes: [
      "Applied the unified learning-area list to the live database so every grid and report now shows the same set of areas. No change to how you use the app.",
    ],
  },
  {
    version: "2.1.22",
    date: "2026-08-16",
    title: "One consistent list of learning areas everywhere",
    changes: [
      "Unified curriculum/learning areas into a single shared list so grids, filters, dropdowns and reports all show the same areas (including RUAH and Retreat).",
      "Every class now has a learning area set, and area names are consistent (e.g. no more mixed 'MATHEMATICS' vs 'Mathematics'). This also clears the recent Grid editor crash for good.",
    ],
  },
  {
    version: "2.1.21",
    date: "2026-08-14",
    title: "Grid editor handles classes with no curriculum area",
    changes: [
      "Fixed a further Grid editor crash (\"Cannot read properties of undefined\") when a class had no curriculum area set. Those classes now render with a neutral colour instead of blanking the page.",
    ],
  },
  {
    version: "2.1.20",
    date: "2026-08-14",
    title: "Grid editor no longer crashes on empty classes",
    changes: [
      "Fixed the Timetable/Grid editor showing a blank screen with a \"Cannot convert undefined or null to object\" error when a grid contained a class with no line assignments yet. Such classes are now handled gracefully.",
    ],
  },
  {
    version: "2.1.19",
    date: "2026-08-14",
    title: "More reliable Microsoft sign-in",
    changes: [
      "Fixed sign-in occasionally failing with an \"authentication timeout\" message. A brief slowdown while loading your profile no longer signs you back out — you'll be let in and your details fill in a moment later.",
      "Removed a spurious \"authentication timeout\" warning that could appear even after you'd successfully signed in.",
    ],
  },
  {
    version: "2.1.18",
    date: "2026-08-13",
    title: "Conflict resolutions behave honestly",
    changes: [
      "Resolving a conflict now sticks — a resolved conflict stays resolved after you re-check or reload (you can Reopen it to undo).",
      "The \"review FTE\" and \"review allowances\" options on workload conflicts no longer pretend to resolve the conflict; they now simply take you to the Staff Directory or Allowances page and leave the conflict open until you make a real change.",
    ],
  },
  {
    version: "2.1.17",
    date: "2026-08-13",
    title: "Conflicts show teacher names",
    changes: [
      "The Conflicts page now shows each teacher as \"First Last (CODE)\" — e.g. \"Joseph Williams (JWS)\" — instead of just the code. It also refreshes once staff data has loaded, so names appear reliably.",
    ],
  },
  {
    version: "2.1.16",
    date: "2026-08-13",
    title: "Conflicts read multi-teacher classes correctly",
    changes: [
      "Fixed the Conflicts page showing raw data (e.g. a bracketed list of codes) instead of a teacher's name for classes that have more than one teacher. Those classes are now read correctly, so double-booking and workload conflicts show the right teachers and their names.",
    ],
  },
  {
    version: "2.1.15",
    date: "2026-08-13",
    title: "Tidier menu and conflicts that stay resolved",
    changes: [
      "The \"Advanced\" tools in the Timetable & Allocation menu are now tucked into a collapsible group that starts closed, so the everyday pages are front and centre. It opens automatically if you're on one of those pages.",
      "On the Conflicts page, marking a conflict as Resolved or Ignored now sticks — it stays that way when you re-check conflicts or reload, instead of reappearing.",
    ],
  },
  {
    version: "2.1.14",
    date: "2026-08-13",
    title: "Assignment History now records real changes",
    changes: [
      "The Assignment History page now shows a real, live audit trail: every time a teacher is assigned, unassigned or changed on the timetable, it's recorded with who did it and when. Previously the page had nothing to show because those changes weren't being logged.",
    ],
  },
  {
    version: "2.1.13",
    date: "2026-08-13",
    title: "Removed three unused timetable pages",
    changes: [
      "Removed the \"Publish to Curriculum\", \"AI Suggestions\" and \"Line Times\" pages. Each saved or showed data that nothing else in the app used, so they didn't affect your timetable — removing them makes the Timetable & Allocation menu clearer.",
    ],
  },
  {
    version: "2.1.12",
    date: "2026-08-13",
    title: "Tidied the Timetable & Allocation menu",
    changes: [
      "Grouped the occasional setup tools (Import Grid, Subject Availability, Line Times) and the bulk/history tools under an \"Advanced\" heading, so the everyday pages stand out.",
      "Removed two menu items that weren't doing anything useful: \"Publish to Curriculum\" (it saved records nothing else read) and \"AI Suggestions\" (its AI feature was never actually built).",
    ],
  },
  {
    version: "2.1.11",
    date: "2026-08-13",
    title: "Grid changes show immediately again",
    changes: [
      "Creating, editing, cloning or deleting a grid now updates the grid lists and the open grid straight away, instead of only showing after a page reload.",
    ],
  },
  {
    version: "2.1.10",
    date: "2026-08-13",
    title: "Faster loading after the central-database move",
    changes: [
      "The dashboard and other screens now load quickly again — a missing database index was making every page do extra work after the move to the shared central database.",
    ],
  },
  {
    version: "2.1.9",
    date: "2026-08-13",
    title: "Fix: dashboard was failing to load after the central-database move",
    changes: [
      "Fixed the dashboard coming up blank after the move to the shared central database — some dashboard queries were timing out. They now run quickly and the dashboard loads.",
    ],
  },
  {
    version: "2.1.8",
    date: "2026-08-13",
    title: "Quieter console for normal 'nothing found' states",
    changes: [
      "When there's no open intentions period, no saved AI configuration, or you haven't submitted an intention yet, the app now treats that as the normal empty state instead of logging a red error. No change to behaviour — just cleaner diagnostics.",
    ],
  },
  {
    version: "2.1.7",
    date: "2026-08-13",
    title: "Internal improvements",
    changes: [
      "Behind-the-scenes: grids are now loaded once and shared across the app instead of being fetched three separate times. No change to how you use it.",
    ],
  },
  {
    version: "2.1.6",
    date: "2026-08-13",
    title: "Fix: fonts and error reporting after the database move",
    changes: [
      "The app's fonts now load correctly again — a security setting was blocking them after the central-database move, so the app had fallen back to plain system fonts.",
      "Re-enabled background error reporting so problems can be diagnosed faster.",
    ],
  },
  {
    version: "2.1.5",
    date: "2026-08-13",
    title: "Fix: sign-in and data loading after the central-database move",
    changes: [
      "Fixed a problem that stopped the app connecting to the database after the move to the shared central database — sign-in and every screen failed to load. The app is now allowed to talk to the new database, so login and data work again.",
    ],
  },
  {
    version: "2.1.4",
    date: "2026-08-13",
    title: "Internal improvements",
    changes: [
      "Behind-the-scenes: grids are now read through one shared definition, so a grid looks the same everywhere in the app. No change to how you use it.",
    ],
  },
  {
    version: "2.1.3",
    date: "2026-08-13",
    title: "Subject-line availability now lives with your classes",
    changes: [
      "Which lines a class can run on is now stored with the class itself, so it's set once when you build or import a grid and shows up reliably in the Timetable Editor — no separate setup step needed.",
      "Editing Subject Availability now updates the timetable straight away for grids built with the new flow.",
      "Existing grids are unaffected — they keep working exactly as before.",
    ],
  },
  {
    version: "2.1.2",
    date: "2026-08-13",
    title: "Clearer grid-building menu",
    changes: [
      "Renamed a few timetable menu items to say what they actually do: \"Classes\" is now \"Class List\", \"Line Scheduling\" is now \"Line Times\", and \"Line & Subject Setup\" is now \"Subject Availability\".",
      "The Import Grid page is now just \"Import a Class List\" — it adds classes to a grid you already have (creating a new grid lives in Build a Grid). If your CSV has a Line column, it records each class's line at the same time.",
      "Removed the old \"matrix\" import option, which wrote to the wrong place and never actually created a grid. Use Build a Grid to create a grid, then import your class list.",
    ],
  },
  {
    version: "2.1.1",
    date: "2026-08-12",
    title: "Internal improvements",
    changes: [
      "Behind-the-scenes change to move PIT onto the school's shared central database. No change to how you use the app.",
    ],
  },
  {
    version: "2.1.0",
    date: "2026-08-12",
    title: "A simpler way to build a grid",
    changes: [
      "New \"Build a Grid\" flow under Timetable & Allocation walks you through creating a grid in three steps — name it and pick a template, upload your class list, then jump straight to allocating teachers. No more hunting across separate setup pages to get started.",
      "If your class-list CSV includes a Line column, the grid now records which line each class can run on in the same step — so you no longer need a separate Line & Subject Setup pass after importing.",
      "Fixed grid import so line information in your CSV actually reaches the timetable. Previously imported line data was saved to a place the editor didn't read, so it had no effect.",
      "The dashboard \"build your timetable grid\" step now takes you straight into the new guided flow.",
    ],
  },
  {
    version: "2.0.1",
    date: "2026-08-12",
    title: "Line & Subject Setup now saves properly",
    changes: [
      "Fixed the Line & Subject Setup page so it actually saves your line-to-subject mappings. Previously every save quietly failed with a \"Failed to save to database\" warning and the mappings never reached the timetable, so which subjects were allowed on which line couldn't be updated from this page. It now saves reliably and loads your saved mappings when you open the page.",
    ],
  },
  {
    version: "2.0.0",
    date: "2026-08-10",
    title: "A cleaner, clearer PIT",
    changes: [
      "Consistency pass across the app: the Intentions page now groups staff by the same learning-area names used everywhere else (no more one-off buckets like \"STEAM & Technology\"), submission-status labels read the same on the Intentions, Review and Discuss screens, and report learning-area filters match correctly.",
      "The intention response rate now reads the same everywhere — the Intentions page, Review Intentions, the dashboard and Reports were each calculating it slightly differently (one even counted unsent drafts as responses). They now share one definition: submitted responses as a share of the teaching staff expected to reply.",
      "One consistent term everywhere: what used to be called \"Department\" or \"Curriculum Area\" in different places is now simply \"Learning Area\" across the timetable editor, reports, staff and settings — all drawn from the same single list.",
      "Tidied the learning-area names: combined labels like \"Science / STEAM\" and inconsistent capitalisation are now shown as one clean learning area, so filters and reports group staff and classes correctly.",
      "Redesigned the main menu into clearer sections — Dashboard, Staff, Curriculum, Timetable, Load Letters, Reports and Settings — with plainer names, so it's easier to find what you need.",
      "Tidied the menus: removed developer and one-off setup tools that were never meant for everyday use, and hid duplicate pages.",
      "Refreshed the look and feel — a new sign-in screen, cleaner typography, and a more consistent, polished style throughout.",
      "The Staff Allowances and Timetable Conflicts screens now export a proper CSV file. Fixed the Assignment History details view (which could fail to open) and corrected a couple of exports that were putting every row on a single line.",
      "The side menu now automatically opens the section you're in, so it's always clear where you are.",
      "Following an unknown or out-of-date link now shows a friendly \"page not found\" screen with a way back, instead of a blank page.",
      "Folded the separate Departments settings screen into Learning Areas (School Settings), so there's just one place to manage the list. Plus small accessibility and visual-consistency improvements throughout.",
      "Removed unfinished and demonstration-only screens and sample placeholder data, so every page and button in the app now does something real. A few features that aren't built yet now say so plainly instead of appearing to work.",
      "Fixed several menu links and buttons that led nowhere, and cleaned up screens that showed example data (like sample staff names and past dates) so you only ever see your school's real information.",
      "A brand-new look: a refreshed college navy-and-gold theme, a new PIT logo, clearer typography, and a redesigned navy side menu — the same tools, now much easier on the eye.",
    ],
  },
  {
    version: "1.16.3",
    date: "2026-08-07",
    title: "More reliable editing of submitted intentions",
    changes: [
      "When an admin or assistant principal edits a staff member's submitted intentions, the change and its audit record are now saved together — so a save either fully succeeds or leaves nothing half-saved, and every successful edit is reliably recorded in the audit trail.",
    ],
  },
  {
    version: "1.16.2",
    date: "2026-08-07",
    title: "Fixed: editing a staff member's submitted intentions no longer shows a save error",
    changes: [
      "Admins and assistant principals who edited and saved a staff member's submitted intentions saw an error saying the audit record failed to write. Saving now completes cleanly and the change is recorded in the audit trail.",
    ],
  },
  {
    version: "1.16.1",
    date: "2026-08-06",
    title: "Tidied the 0.4 load option",
    changes: [
      "Removed the \"3 days off per week\" note under the 0.4 FTE option on the staff preferences form.",
    ],
  },
  {
    version: "1.16.0",
    date: "2026-08-06",
    title: "More part-time load options on the preferences form",
    changes: [
      "The Preferred Load question on the staff preferences form now offers 0.8, 0.6 and 0.4, alongside the existing 1.0, 0.75 and 0.5.",
      "0.8 asks which single day you'd like off each week (the same as 0.75), and 0.6 asks which two days (the same as 0.5). 0.4 doesn't ask about days.",
    ],
  },
  {
    version: "1.15.2",
    date: "2026-07-29",
    title: "Fixed: approving or flagging a teaching intention now saves",
    changes: [
      "Approving a submission or flagging it for discussion on the HR review screen failed to save for some reviewers. It now saves correctly and records who reviewed it.",
    ],
  },
  {
    version: "1.15.1",
    date: "2026-07-29",
    title: "Review notes are now private to leadership",
    changes: [
      "Notes added when approving or flagging a teaching intention are now visible only to leadership and HR — the staff member whose submission it is can no longer access them.",
    ],
  },
  {
    version: "1.15.0",
    date: "2026-07-29",
    title: "Filter HR review by staff who asked for a conversation",
    changes: [
      "The HR review screen has a new 'Conversation requested' filter that narrows the list to submissions where the teacher selected \"I'd like to discuss my options\" for their load or day-off preference.",
      "These submissions now also show a small conversation icon next to the staff member's name, so they're easy to spot even when the filter is off.",
    ],
  },
  {
    version: "1.14.1",
    date: "2026-07-16",
    title: "See at a glance which intentions have extra comments",
    changes: [
      "Staff who left a note in the 'Additional Comments' field now show a small comment icon next to their name on the Teaching Intentions list and the HR review screen, so admins and HR can spot at a glance which submissions have extra information to read.",
    ],
  },
  {
    version: "1.14.0",
    date: "2026-07-16",
    title: "Full teaching-intention details, with editing",
    changes: [
      "Viewing a teaching intention now shows the complete submission — employment and return plans, leave, availability, ranked subject and curriculum preferences, TIL activities and comments — instead of a partial summary.",
      "Admins and HR can now edit any part of a submitted intention from both the intentions list and the HR review screen, with every change recorded for audit.",
    ],
  },
  {
    version: "1.13.5",
    date: "2026-06-24",
    title: "Fixed: Completion Rate now reflects how many staff actually submitted",
    changes: [
      "The Overview 'Completion Rate' (and the Insights collection-status note) previously showed ~100% because it only measured staff who had already submitted. It now shows the true rate — submissions received divided by the staff expected to submit — matching the Not Submitted tab.",
    ],
  },
  {
    version: "1.13.4",
    date: "2026-06-24",
    title: "Not Submitted list is now a single alphabetical list",
    changes: [
      "The 'Not Submitted' tab now lists outstanding staff alphabetically by name (with their department shown on each card) instead of grouping them by department.",
    ],
  },
  {
    version: "1.13.3",
    date: "2026-06-24",
    title: "Submissions table now shows everything teachers submit on the form",
    changes: [
      "The Submissions tab now surfaces the full intentions form: FTE preference, day-off preference, ranked curriculum areas, specific subjects, year levels, RUAH subjects, leave plans, Year 9 Stretch interest, TIL activities, and not-returning reasons — not just the basics.",
      "The 'Export all data' file includes all of these fields too.",
    ],
  },
  {
    version: "1.13.2",
    date: "2026-06-24",
    title: "Submissions table now shows teaching preferences for form submissions too",
    changes: [
      "The Teaching Preferences column (and the Excel export) now shows the ranked curriculum areas chosen by staff who submitted through the app form — previously it only filled in for imported submissions and showed blank for everyone else.",
    ],
  },
  {
    version: "1.13.1",
    date: "2026-06-24",
    title: "Fixed: Submissions table now shows the real intention answers",
    changes: [
      "The Submissions table's teaching preferences now display correctly (they were showing blank).",
      "Return intention and employment type now reflect what each person actually submitted, instead of defaulting to 'uncertain'/'full-time'. This also corrects the Overview and Insights tabs for imported submissions.",
      "Removed the 'Current FTE' and 'Desired Employment' columns — those aren't asked on the intentions form. 'Target FTE' (the preferred load for next year) remains.",
    ],
  },
  {
    version: "1.13.0",
    date: "2026-06-24",
    title: "See exactly who hasn't submitted their teaching intentions",
    changes: [
      "Teaching Intentions Reports has a new 'Not Submitted' tab that lists every staff member who hasn't submitted yet for the selected year, grouped by department, with a count badge on the tab.",
      "Anyone who saved a draft but hasn't submitted is clearly marked 'Draft started', so you can tell who began the form from who hasn't engaged at all.",
      "Switch between 'Teaching roles only' (the default) and 'All active staff', and export the outstanding list to CSV for follow-up.",
    ],
  },
  {
    version: "1.12.0",
    date: "2026-06-23",
    title: "New: see and export all teaching intention submissions in one table",
    changes: [
      "The Teaching Intentions report has a new 'Submissions' tab showing every submission for the selected year in a single sortable, filterable table.",
      "Filter the table by status (submitted, draft, imported, HR-reviewed) and search by name, staff ID, or department.",
      "Choose which columns to show with the 'Columns' picker — from the key fields down to every detail captured on the form.",
      "Export the complete data set (every field of every submission) to Excel with one click for offline analysis.",
    ],
  },
  {
    version: "1.11.4",
    date: "2026-06-17",
    title: "Fixed: some staff were locked out of the teaching intentions form",
    changes: [
      "A small number of staff whose sign-in wasn't yet linked to their staff profile couldn't open or submit their teaching intentions. Their account now links automatically the moment they sign in, so the form works normally.",
      "Re-linked staff who had already signed in but were still affected.",
    ],
  },
  {
    version: "1.11.3",
    date: "2026-06-15",
    title: "Fixed: many teachers couldn't submit their teaching intentions",
    changes: [
      "Teachers who were seeing a 'no open submission period' message — even while a period was open — can now submit their intentions normally.",
      "Staff whose account isn't yet linked to a staff profile now see a clear message explaining what to do, instead of being told (incorrectly) that no period was open.",
    ],
  },
  {
    version: "1.11.2",
    date: "2026-06-15",
    title: "Internal improvements",
    changes: [
      "Behind-the-scenes monitoring and logging improvements. No changes to how you use the app.",
    ],
  },
  {
    version: "1.11.1",
    date: "2026-06-15",
    title: "Internal improvements",
    changes: [
      "Behind-the-scenes security and access-control hardening. No changes to how you use the app.",
    ],
  },
  {
    version: "1.11.0",
    date: "2026-06-12",
    title: "Teaching-intentions form now lists every subject we offer",
    changes: [
      "When nominating specific subjects, teachers now see the full list of subjects offered on the timetable grid — not a partial list. Subjects that were previously missing (for example FOCUS - SCIENCE - BIOLOGY) now appear.",
      "Each subject is listed once, even when several classes of it run.",
    ],
  },
  {
    version: "1.10.7",
    date: "2026-06-12",
    title: "HR review now shows the full teaching-intention submission",
    changes: [
      "When reviewing a staff member's teaching intentions, the review window now shows everything they submitted — return/employment intention, planned leave, preferred load and day-off preference, ranked curriculum areas, subjects and RUAH subjects, year levels, and their chosen TIL activities. Previously only a few of these were shown.",
      "Submissions received after the due date are now clearly marked 'Late' in the review window.",
    ],
  },
  {
    version: "1.10.6",
    date: "2026-06-12",
    title: "Fixed: teachers couldn't see the 'Submit my intentions' button when a period opened",
    changes: [
      "When a teaching-intentions submission period is opened, teachers now reliably see the 'Submit now' banner and button without needing to refresh the page. This also fixes the same button not appearing when an administrator viewed the app as a teacher.",
    ],
  },
  {
    version: "1.10.5",
    date: "2026-06-11",
    title: "Fixed: admins couldn't save staff, department or allowance changes",
    changes: [
      "Restored the ability for admins to add, edit and delete staff members, departments and allowance templates. A database security change in the previous release accidentally blocked all of these saves with a permission error — this corrects it.",
    ],
  },
  {
    version: "1.10.4",
    date: "2026-06-09",
    title: "Internal: database security hardening",
    changes: [
      "Tightened database-level permissions on internal helper functions and the MyHR teaching-intentions view. No user-facing change.",
    ],
  },
  {
    version: "1.10.3",
    date: "2026-06-09",
    title: "Security: AI suggestions now run through a secure server function",
    changes: [
      "The OpenAI key for AI suggestions is no longer held in the browser — requests go through a secure server-side function that checks you're an active staff member. No change to how the feature is used.",
    ],
  },
  {
    version: "1.10.2",
    date: "2026-06-09",
    title: "Admin impersonation is now verified and audited on the server",
    changes: [
      "Starting 'impersonate user' is now authorised on the server, not just in the browser, and every start/stop is recorded in an audit log so it's always clear which administrator viewed the app as which staff member.",
      "Impersonation now uses the staff member's real account details, fixing occasional glitches caused by the previous temporary placeholder identity.",
    ],
  },
  {
    version: "1.10.1",
    date: "2026-06-11",
    title: "Staff FTE report now shows the allowance fraction explicitly",
    changes: [
      "The Staff FTE (Time Fraction) report has a new 'Allowance FTE' column between Teaching FTE and Total FTE. Teaching FTE + Allowance FTE now add up to Total FTE on screen, so when a teacher's classroom load sits below their contract you can see exactly how much is going to allowances rather than having to work it out.",
    ],
  },
  {
    version: "1.10.0",
    date: "2026-06-11",
    title: "New Staff FTE (Time Fraction) report",
    changes: [
      "Added 'Staff FTE (Time Fraction)' under Reports → Staff (and in the sidebar). It lists every active staff member with their contracted FTE alongside two worked-out figures for the chosen semester: Teaching FTE (classroom load only) and Total FTE (teaching plus allowances such as POL, coordination and mentoring).",
      "Defaults to Semester 2, with Semester 1 / Semester 2, department, and active-only filters, sortable columns, and CSV/Excel/PDF export (the export also breaks out teaching, allowance and allocated minutes).",
      "The effective figures come from released load letters, so staff without a released letter for the semester show '—'.",
    ],
  },
  {
    version: "1.9.5",
    date: "2026-06-10",
    title: "Run the Timetabled Subs report by semester",
    changes: [
      "The Timetabled Subs Total report now has a Semester filter (Semester 1, Semester 2, or Both combined). Previously it always added both semesters together. It opens on the current semester by default, and the chosen semester is shown in the exported CSV/Excel/PDF.",
    ],
  },
  {
    version: "1.9.4",
    date: "2026-06-10",
    title: "Load letters now pro-rate allowances by FTE, matching the grid",
    changes: [
      "Allowance minutes on load letters are now scaled by each staff member's FTE, so the letter matches the figures shown in the grid and load-summary views. Previously the letter showed the raw entered minutes while the grid scaled them, which could disagree for part-time staff.",
      "After this update, regenerate and re-release affected load letters so they reflect the corrected allowance figures.",
    ],
  },
  {
    version: "1.9.3",
    date: "2026-06-10",
    title: "Load letter generation now shows one semester at a time",
    changes: [
      "On the load letter generation page, the Semester selector now filters the list of letters, so each staff member no longer appears twice (once for Semester 1 and once for Semester 2). Choose 'Full Year' to see every letter together.",
      "Added a Semester column to the letters table so it's always clear which letter is which.",
      "Switching the semester now clears any selected rows, so letters from the other semester can't accidentally be included in a bulk release.",
    ],
  },
  {
    version: "1.9.2",
    date: "2026-06-09",
    title: "Load letter generation defaults to the current semester",
    changes: [
      "The semester selector on the load letter generation page now defaults to the current academic period instead of always Semester 1 — so from mid-year it pre-selects Semester 2. You can still change it before generating.",
    ],
  },
  {
    version: "1.9.1",
    date: "2026-06-09",
    title: "Load letters show the correct RUAH term per semester",
    changes: [
      "On Semester 2 load letters, the RUAH load now correctly references Term 3 (with its minutes) instead of always saying 'Term 1'. The RUAH classes table also highlights the term that actually counts for the letter's semester.",
    ],
  },
  {
    version: "1.9.0",
    date: "2026-06-09",
    title: "Easier access to the teaching intentions form",
    changes: [
      "When you open a link to the teaching intentions form before signing in, you now continue straight to the form after signing in, instead of landing on the dashboard.",
      "While a submission period is open, a 'Submit My Teaching Intentions' button appears in the top bar for all staff, and teachers also see a reminder banner on their dashboard.",
    ],
  },
  {
    version: "1.8.5",
    date: "2026-06-09",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release. Tidied up a duplicate database constraint behind the scenes.",
    ],
  },
  {
    version: "1.8.4",
    date: "2026-06-09",
    title: "Clearer message when adding a staff member who already exists",
    changes: [
      "When you try to add a user whose email is already in the system, you now get a plain-language message telling you they already exist (and to search for them), instead of a confusing database error.",
    ],
  },
  {
    version: "1.8.3",
    date: "2026-05-28",
    title: "RUAH Term Comparison now shows the subject",
    changes: [
      "Each row on the RUAH Term Comparison report now shows the RUAH subject the teacher is assigned to in each term, alongside the minutes. Multiple RUAHs in a single term are listed comma-separated. The export (CSV/Excel/PDF) includes the new columns as well.",
    ],
  },
  {
    version: "1.8.2",
    date: "2026-05-28",
    title: "RUAH Term Comparison now shows data",
    changes: [
      "Fixed the RUAH Term Comparison report appearing empty. It now correctly lists every teacher with RUAH minutes in either of the compared terms.",
    ],
  },
  {
    version: "1.8.1",
    date: "2026-05-28",
    title: "Daily Organiser reports added to the sidebar",
    changes: [
      "The 'RUAH Term Comparison' and 'Timetabled Subs Total' reports now appear in the main sidebar under Reports & Analytics, so you no longer need the direct URL to open them.",
    ],
  },
  {
    version: "1.8.0",
    date: "2026-05-28",
    title: "New reports for the Daily Organiser",
    changes: [
      "Added 'RUAH Term Comparison' under Reports → Staff. Pick any two terms (T1↔T2, T2↔T3, or T3↔T4) and compare RUAH minutes per teacher, with department filter and CSV/Excel/PDF export.",
      "Added 'Timetabled Subs Total' under Reports → Staff. Shows the total under-allocation (subs) minutes per teacher for a chosen year, summed across their released load letters.",
    ],
  },
  {
    version: "1.7.8",
    date: "2026-05-16",
    title: "Safer rendering of load letter previews",
    changes: [
      "Load letter previews are now sanitised before display, preventing any embedded HTML or scripts from running in your browser.",
    ],
  },
  {
    version: "1.7.7",
    date: "2026-05-16",
    title: "Stronger protection against staff role self-escalation",
    changes: [
      "Tightened database access rules so that staff can no longer change their own role, status, email, permissions, or login links from the browser. Only admins can change these fields, and only via the User Management screen as intended. (Previously a database trigger blocked this; now the access policy itself blocks it too as an extra layer.)",
    ],
  },
  {
    version: "1.7.6",
    date: "2026-05-16",
    title: "User management now reliably reaches Supabase",
    changes: [
      "Inviting, creating and deleting users now goes through a secure server-side path. Previously these actions could silently fail without telling you. If you've been unable to remove a compromised account in the past, please try again — and audit existing access.",
    ],
  },
  {
    version: "1.7.5",
    date: "2026-04-29",
    title: "Add in-app version display",
    changes: [
      "You can now see the app version in the bottom-right corner of any page. Click it to see what's changed in each release.",
    ],
  },
];
